import { fetchApi } from '../api';

class AuditLogDecryption {
  constructor() {
    this.decryptedCache = new Map();
  }

  // Check if a field is encrypted (DynamoDB format)
  isEncrypted(field) {
    return field && typeof field === 'object' && field.encryptedKey && field.encryptedData;
  }

  // Decrypt audit logs by calling backend
  async decryptAuditLogs(logs) {
    try {
      const decryptedLogs = await Promise.all(logs.map(async (log) => {
        const cacheKey = `${log.esl_salary_code}_${log.esl_id}`;
        
        // Check cache first
        if (this.decryptedCache.has(cacheKey)) {
          return this.decryptedCache.get(cacheKey);
        }

        const decryptedLog = { ...log };
        
        // Check if any PII fields need decryption
        const needsDecryption = this.isEncrypted(log.esl_telephone_no) || 
                               this.isEncrypted(log.esl_mobile_no);

        if (needsDecryption) {
          try {
            // Call backend to decrypt the audit log entry
            const response = await fetchApi('/api/signature/decrypt-audit-log', {
              method: 'POST',
              body: JSON.stringify({ logData: log })
            });
            
            if (!response.didError && response.data) {
              // Use decrypted data from backend
              Object.assign(decryptedLog, response.data);
            } else {
              // Fallback to showing [ENCRYPTED] labels
              if (this.isEncrypted(log.esl_telephone_no)) {
                decryptedLog.esl_telephone_no = '[ENCRYPTED TEL]';
              }
              if (this.isEncrypted(log.esl_mobile_no)) {
                decryptedLog.esl_mobile_no = '[ENCRYPTED MOB]';
              }
            }
          } catch (error) {
            console.error('Failed to decrypt log fields via backend:', error);
            if (this.isEncrypted(log.esl_telephone_no)) {
              decryptedLog.esl_telephone_no = '[DECRYPT ERROR]';
            }
            if (this.isEncrypted(log.esl_mobile_no)) {
              decryptedLog.esl_mobile_no = '[DECRYPT ERROR]';
            }
          }
        }

        // Cache the result
        this.decryptedCache.set(cacheKey, decryptedLog);
        return decryptedLog;
      }));

      return decryptedLogs;
    } catch (error) {
      console.error('Error decrypting audit logs:', error);
      return logs; // Return original logs on error
    }
  }

  // Clear cache
  clearCache() {
    this.decryptedCache.clear();
  }

  // Get decrypted logs for a specific employee
  async getDecryptedLogsForEmployee(salaryCode, allLogs) {
    const employeeLogs = allLogs.filter(log => log.esl_salary_code === salaryCode);
    return await this.decryptAuditLogs(employeeLogs);
  }
}

// Create singleton instance
const auditLogDecryption = new AuditLogDecryption();

export default auditLogDecryption;